/**
 * Register search, view options and sorting (screens.md §4). Pure functions, so the desktop table and the
 * mobile list filter the same way and the rules are unit-tested.
 *
 * A search is a list of tokens plus the text still being typed. Tokens on the same field are OR'd
 * ("Account: A" or "Account: B"); different fields are AND'd. Every "any field" token must match.
 */
import {
  FLAGS,
  addMonths,
  firstDay,
  lastDay,
  minorToDecimalString,
  monthOf,
  type Flag,
  type IsoDate,
  type Month,
  type TransactionDto,
} from '@koverta/shared';

export type Field = 'any' | 'account' | 'category' | 'payee' | 'memo' | 'amount' | 'is' | 'flag' | 'member';

/** `id` pins an exact entity (picked from the suggestions); without it, `value` is a "contains" match. */
export interface Token {
  field: Field;
  value: string;
  id?: string;
}

export const IS_OPTIONS = [
  ['unapproved', 'Unapproved'],
  ['approved', 'Approved'],
  ['uncleared', 'Uncleared'],
  ['cleared', 'Cleared'],
  ['reconciled', 'Reconciled'],
  ['flagged', 'Flagged'],
  ['transfer', 'Transfer'],
  ['split', 'Split'],
  ['uncategorized', 'Needs a category'],
  ['scheduled', 'From a schedule'],
  ['captured', 'Captured'],
  ['imported', 'Imported'],
] as const;
export type IsKey = (typeof IS_OPTIONS)[number][0];

export const FIELD_LABEL: Record<Field, string> = {
  any: 'Any field',
  account: 'Account',
  category: 'Category',
  payee: 'Payee',
  memo: 'Memo',
  amount: 'Amount',
  is: 'Is',
  flag: 'Flag',
  member: 'Added by',
};

/** Lowercase and strip diacritics, so "tekuci" finds "Tekući" and "dj" finds "Đ". */
export const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/đ/g, 'dj')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export interface FilterCtx {
  /** Tracking accounts never take a category, so a missing one doesn't count as "Needs a category". */
  tracking: (accountId: string) => boolean;
}

function amountMatches(amount: number, needle: string) {
  const digits = needle.replace(/[^\d]/g, '');
  if (!digits) return false;
  const abs = Math.abs(amount);
  return (
    String(abs).startsWith(digits) ||
    minorToDecimalString(abs)
      .replace(/\D/g, '')
      .replace(/^0+(?=\d)/, '')
      .startsWith(digits)
  );
}

const categoryNames = (t: TransactionDto) => [
  t.transfer && !t.categoryName ? 'Transfer' : t.categoryName,
  ...t.splits.map((sp) => sp.categoryName),
];

function isMatch(t: TransactionDto, key: string, ctx: FilterCtx): boolean {
  switch (key as IsKey) {
    case 'unapproved':
      return !t.approved;
    case 'approved':
      return t.approved;
    case 'uncleared':
      return t.cleared === 'uncleared';
    case 'cleared':
      return t.cleared === 'cleared';
    case 'reconciled':
      return t.cleared === 'reconciled';
    case 'flagged':
      return !!t.flag;
    case 'transfer':
      return !!t.transfer;
    case 'split':
      return t.splits.length > 0;
    case 'uncategorized':
      return (
        !ctx.tracking(t.accountId) &&
        (t.splits.length
          ? t.splits.some((sp) => !sp.categoryId)
          : !t.transfer && !t.categoryId && !t.categoryName)
      );
    case 'scheduled':
      return !!t.scheduledId;
    case 'captured':
      return !!t.captureId;
    case 'imported':
      return !!t.importId;
  }
  return false;
}

export function tokenMatches(t: TransactionDto, k: Token, ctx: FilterCtx): boolean {
  const v = norm(k.value.trim());
  const has = (s: string) => norm(s).includes(v);
  switch (k.field) {
    case 'account':
      return k.id ? t.accountId === k.id : has(t.accountName);
    case 'category':
      return k.id
        ? t.categoryId === k.id || t.splits.some((sp) => sp.categoryId === k.id)
        : categoryNames(t).some(has);
    case 'payee':
      return k.id ? norm(t.payeeName) === norm(k.id) : has(t.payeeName);
    case 'memo':
      return has(t.memo) || t.splits.some((sp) => has(sp.memo));
    case 'amount':
      return amountMatches(t.amount, v) || t.splits.some((sp) => amountMatches(sp.amount, v));
    case 'is':
      return isMatch(t, k.id ?? '', ctx);
    case 'flag':
      return t.flag === k.id;
    case 'member':
      return t.createdBy === k.id;
    case 'any':
      return (
        !v ||
        has(t.payeeName) ||
        has(t.memo) ||
        has(t.accountName) ||
        categoryNames(t).some(has) ||
        t.splits.some((sp) => has(sp.memo) || amountMatches(sp.amount, v)) ||
        amountMatches(t.amount, v)
      );
  }
}

/** "payee: lid" → a payee "contains" token; anything else searches every field. */
export function parseTyped(text: string): Token | null {
  const s = text.trim();
  if (!s) return null;
  const m = /^(account|category|payee|memo|amount)\s*:\s*(.*)$/i.exec(s);
  if (m) return m[2]!.trim() ? { field: m[1]!.toLowerCase() as Field, value: m[2]!.trim() } : null;
  return { field: 'any', value: s };
}

export function matchesSearch(t: TransactionDto, tokens: Token[], ctx: FilterCtx): boolean {
  const byField = new Map<Field, Token[]>();
  for (const k of tokens) {
    if (k.field === 'any') {
      if (!tokenMatches(t, k, ctx)) return false;
    } else byField.set(k.field, [...(byField.get(k.field) ?? []), k]);
  }
  for (const group of byField.values()) if (!group.some((k) => tokenMatches(t, k, ctx))) return false;
  return true;
}

// ───────── Suggestions ─────────

export interface Suggestion {
  token: Token;
  /** Left column, YNAB-style ("Account:"); empty for "Find … in any field". */
  group: string;
  label: string;
  /** Generic "Find x in the …" rows render the needle in bold. */
  find?: boolean;
  icon?: 'flag' | 'avatar' | IsIcon;
}
type IsIcon = 'inbox' | 'check' | 'x' | 'lock' | 'swap' | 'split' | 'target' | 'cal' | 'phone' | 'upload';
export const IS_ICON: Record<IsKey, IsIcon | 'flag'> = {
  unapproved: 'inbox',
  approved: 'check',
  uncleared: 'x',
  cleared: 'check',
  reconciled: 'lock',
  flagged: 'flag',
  transfer: 'swap',
  split: 'split',
  uncategorized: 'target',
  scheduled: 'cal',
  captured: 'phone',
  imported: 'upload',
};

export interface SuggestSource {
  accounts?: { id: string; name: string }[];
  categories: { id: string; name: string }[];
  payees: string[];
  members: { id: string; displayName: string }[];
}

/** Starts-with first, then word-start, then anywhere; ties keep source order. */
function rank<T>(items: T[], name: (x: T) => string, needle: string, max: number): T[] {
  const n = norm(needle);
  const score = (s: string) => {
    const h = norm(s);
    if (h.startsWith(n)) return 0;
    if (new RegExp(`[\\s:(/·-]${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(h)) return 1;
    return h.includes(n) ? 2 : -1;
  };
  return items
    .map((x, i) => ({ x, i, s: score(name(x)) }))
    .filter((r) => r.s >= 0)
    .sort((a, b) => a.s - b.s || a.i - b.i)
    .slice(0, max)
    .map((r) => r.x);
}

export function suggest(text: string, src: SuggestSource, tokens: Token[]): Suggestion[] {
  const typed = parseTyped(text);
  if (!typed) return [];
  const only = typed.field === 'any' ? null : typed.field;
  const needle = typed.value;
  const taken = (field: Field, id: string) => tokens.some((k) => k.field === field && k.id === id);
  const out: Suggestion[] = [];
  const want = (f: Field) => !only || only === f;
  if (want('account') && src.accounts)
    for (const a of rank(src.accounts, (a) => a.name, needle, 12))
      if (!taken('account', a.id))
        out.push({ token: { field: 'account', id: a.id, value: a.name }, group: 'Account', label: a.name });
  if (want('category'))
    for (const c of rank(src.categories, (c) => c.name, needle, 8))
      if (!taken('category', c.id))
        out.push({ token: { field: 'category', id: c.id, value: c.name }, group: 'Category', label: c.name });
  if (want('payee'))
    for (const p of rank(src.payees, (p) => p, needle, 6))
      if (!taken('payee', p))
        out.push({ token: { field: 'payee', id: p, value: p }, group: 'Payee', label: p });
  if (!only) {
    for (const [id, label] of rank([...IS_OPTIONS], ([, l]) => l, needle, 12))
      if (!taken('is', id))
        out.push({ token: { field: 'is', id, value: label }, group: 'Is', label, icon: IS_ICON[id] });
    for (const f of rank([...FLAGS], (f) => f, needle, 6))
      if (!taken('flag', f))
        out.push({ token: { field: 'flag', id: f, value: f }, group: 'Flag', label: f, icon: 'flag' });
    for (const m of rank(src.members, (m) => m.displayName, needle, 2))
      if (!taken('member', m.id))
        out.push({
          token: { field: 'member', id: m.id, value: m.displayName },
          group: 'Added by',
          label: m.displayName,
          icon: 'avatar',
        });
  }
  const find = (field: Field, group: string, where: string) =>
    out.push({ token: { field, value: needle }, group, label: where, find: true });
  if (!only) find('any', '', 'any field');
  if (want('payee')) find('payee', 'Payee', 'the Payee');
  if (want('category')) find('category', 'Category', 'the Category');
  if (want('memo')) find('memo', 'Memo', 'the Memo');
  if (want('account') && src.accounts) find('account', 'Account', 'the Account');
  if (want('amount') && /\d/.test(needle)) find('amount', 'Amount', 'the Amount');
  return out;
}

export function tokenLabel(k: Token): string {
  if (k.field === 'any') return `“${k.value}”`;
  if (k.id || k.field === 'is' || k.field === 'flag' || k.field === 'member')
    return `${FIELD_LABEL[k.field]}: ${k.value}`;
  return `${FIELD_LABEL[k.field]} contains “${k.value}”`;
}

// ───────── View options ─────────

export const RANGE_PRESETS = [
  ['thisMonth', 'This Month'],
  ['lastMonth', 'Last Month'],
  ['last3', 'Latest 3 Months'],
  ['thisYear', 'This Year'],
  ['lastYear', 'Last Year'],
  ['all', 'All Dates'],
] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number][0];
export type Range = { preset: RangePreset } | { from: Month; to: Month };

export interface RegisterView {
  range: Range;
  showReconciled: boolean;
  showFlags: boolean;
  showMemo: boolean;
  showUpcoming: boolean;
}
export const DEFAULT_VIEW: RegisterView = {
  range: { preset: 'all' },
  showReconciled: true,
  showFlags: true,
  showMemo: true,
  showUpcoming: true,
};

/** Inclusive month bounds of a range; `null` means unbounded on that side. */
export function rangeMonths(r: Range, today: IsoDate): { from: Month | null; to: Month | null } {
  if (!('preset' in r)) return { from: r.from, to: r.to };
  const m = monthOf(today);
  const y = today.slice(0, 4);
  switch (r.preset) {
    case 'thisMonth':
      return { from: m, to: m };
    case 'lastMonth':
      return { from: addMonths(m, -1), to: addMonths(m, -1) };
    case 'last3':
      return { from: addMonths(m, -2), to: m };
    case 'thisYear':
      return { from: `${y}-01`, to: `${y}-12` };
    case 'lastYear':
      return { from: `${Number(y) - 1}-01`, to: `${Number(y) - 1}-12` };
    case 'all':
      return { from: null, to: null };
  }
}

export function inView(t: TransactionDto, v: RegisterView, today: IsoDate): boolean {
  if (!v.showReconciled && t.cleared === 'reconciled') return false;
  const { from, to } = rangeMonths(v.range, today);
  if (from && t.date < firstDay(from)) return false;
  if (to && t.date > lastDay(to)) return false;
  return true;
}

export const isDefaultView = (v: RegisterView) =>
  'preset' in v.range &&
  v.range.preset === 'all' &&
  v.showReconciled &&
  v.showFlags &&
  v.showMemo &&
  v.showUpcoming;

// ───────── Sorting ─────────

export type SortKey =
  'flag' | 'date' | 'payee' | 'account' | 'category' | 'memo' | 'outflow' | 'inflow' | 'cleared';
export interface Sort {
  key: SortKey;
  dir: 'asc' | 'desc';
}
export const DEFAULT_SORT: Sort = { key: 'date', dir: 'desc' };
/** Dates and amounts open newest / largest first; text columns open A→Z. */
export const firstDir = (k: SortKey): Sort['dir'] =>
  k === 'date' || k === 'outflow' || k === 'inflow' || k === 'flag' ? 'desc' : 'asc';

const CLEARED_ORDER = { uncleared: 0, cleared: 1, reconciled: 2 } as const;

export const categoryText = (t: TransactionDto) =>
  t.splits.length ? 'Split' : t.transfer && !t.categoryName ? 'Transfer' : t.categoryName;

/**
 * Stable sort. The input arrives newest first, so ties keep that order. Empty text sorts last in both
 * directions; amounts compare in base currency across accounts (`base`), natively within one.
 */
export function sortTxs(list: TransactionDto[], sort: Sort, base: boolean): TransactionDto[] {
  if (sort.key === 'date' && sort.dir === 'desc') return list;
  const amt = (t: TransactionDto) => (base ? t.baseAmount : t.amount);
  const sign = sort.dir === 'asc' ? 1 : -1;
  const text = (f: (t: TransactionDto) => string) => (a: TransactionDto, b: TransactionDto) => {
    const x = f(a);
    const y = f(b);
    if (!x || !y) return x ? -1 : y ? 1 : 0;
    return sign * x.localeCompare(y, undefined, { sensitivity: 'base', numeric: true });
  };
  const num = (f: (t: TransactionDto) => number) => (a: TransactionDto, b: TransactionDto) =>
    sign * (f(a) - f(b));
  const cmp = {
    flag: num((t) => (t.flag ? FLAGS.length - FLAGS.indexOf(t.flag as Flag) : 0)),
    date: (a: TransactionDto, b: TransactionDto) => sign * a.date.localeCompare(b.date),
    payee: text((t) => t.payeeName),
    account: text((t) => t.accountName),
    category: text(categoryText),
    memo: text((t) => t.memo),
    outflow: num((t) => -amt(t)),
    inflow: num(amt),
    cleared: num((t) => CLEARED_ORDER[t.cleared]),
  }[sort.key];
  return list
    .map((t, i) => ({ t, i }))
    .sort((a, b) => cmp(a.t, b.t) || a.i - b.i)
    .map((r) => r.t);
}
